import React, { useState, useEffect, useMemo, useRef } from 'react'
import Select from 'react-select'
import { FormControl, Button } from 'react-bootstrap'
import ThaiIDCard from './ThaiIDCard'
import io from 'socket.io-client'
import axios from 'axios'
import objectPath from 'object-path'
import httpClient from 'utility/httpClient.util'
import { debounceCallback } from 'utility/debounceCallback.util'
import {
  DESKTOP_OFFLINE_MESSAGE,
  DESKTOP_SERVICE_URL,
  ensureDesktopService,
} from 'utility/desktopService.util'
import { InfoOutlined } from '@mui/icons-material'
import { IconButton, Tooltip } from '@material-ui/core'
import { useTimeFormat } from 'contexts/TimeFormatContext'
import {
  mergeNonEmptySmartCardData,
  mergePersonWithSmartCardProfile,
  normalizeSmartCardAddressData,
} from './smartCardProfile.util'
import {
  findSmartCardAddressRecord,
  getSmartCardAddressRecordName,
  getSmartCardAddressText,
  getSmartCardHouseNumber,
  getSmartCardVillageCode,
} from './smartCardAddress.util'

const DEFAULT_CARD_DATA = {
  cid: '-',
  th: { prefix_name: '-', first_name: '-', last_name: '-' },
  en: { prefix_name: '-', first_name: '-', last_name: '-' },
  gender: '',
  birth_date: '-',
  address: '-',
  issue_date: '-',
  expire_date: '-',
  photo: null,
}

const ScanSmartCard = ({
  styles,
  designerData = {},
  onUpdateData,
  onUpdateDesignerData,
  onCreateCallback,
  designerKey,
  religionList = [],
  ...props
}) => {
  const { formatDate, formatDateTime } = useTimeFormat()
  const [message, setMessage] = useState('กำลังประมวลผล...')
  const [tip, setTip] = useState('')
  const [result, setResult] = useState('')
  const [errorMessage, setErrorMessage] = useState('')
  const [cardData, setCardData] = useState({ ...DEFAULT_CARD_DATA })
  const [showInfo, setShowInfo] = useState(false)
  const [patientList, setPatientList] = useState([])
  const [selectedPatient, setSelectedPatient] = useState(null)
  const [isSearchingHN, setIsSearchingHN] = useState(false)
  const [hasSearchedHN, setHasSearchedHN] = useState(false)
  const [hnSearchMatchType, setHnSearchMatchType] = useState('none')
  const [searchedCitizenId, setSearchedCitizenId] = useState('')

  const [isReadyApp, setIsReadyApp] = useState(false)
  const patientDetailCacheRef = useRef({})
  const hnSearchMatchTypeRef = useRef('none')
  const processedSmartCardCidRef = useRef('')
  const loggedSmartCardCidRef = useRef('')
  const addressTransformCacheRef = useRef({})
  const selectedPatientHn = useMemo(
    () => (selectedPatient ? objectPath.get(selectedPatient, 'hn') : null),
    [selectedPatient],
  )

  // Bring the desktop service up, then arm the reader. The probe is
  // ensureDesktopService's GET /health: probing with POST /smart-cards (what
  // this did before) starts a real card read as a side effect.
  const startReader = async () => {
    try {
      const resp = (await axios.post(`${DESKTOP_SERVICE_URL}/smart-cards`)).data
      if (resp.status === 'processing') {
        setMessage('รอสักครู่ เรียกคำสั่งอ่านบัตรประชาชนเรียบร้อย...')
        setTip('')
        setErrorMessage('')
      }
      setIsReadyApp(true)
      return true
    } catch (err) {
      return false
    }
  }

  useEffect(() => {
    const abortController = new AbortController()

    ensureDesktopService({
      signal: abortController.signal,
      onStatus: ({ text }) => {
        if (abortController.signal.aborted) return
        setMessage(text)
        setTip('')
        setErrorMessage('')
      },
    }).then(async (ready) => {
      if (abortController.signal.aborted) return
      if (!ready.ok) {
        setMessage(DESKTOP_OFFLINE_MESSAGE)
        setTip('')
        setErrorMessage('')
        return
      }
      if (!(await startReader()) && !abortController.signal.aborted) {
        setMessage(DESKTOP_OFFLINE_MESSAGE)
      }
    })

    return () => abortController.abort()
  }, [])

  const getPatientList = async (url) => {
    let patient = await httpClient
      .get(url)
      .then(({ data }) => data.data)
      .catch(() => [])
    return Array.isArray(patient) ? patient : []
  }

  const getCardFullName = (data) => {
    return [
      objectPath.get(data, 'th.first_name'),
      objectPath.get(data, 'th.last_name'),
    ]
      .filter(Boolean)
      .join(' ')
      .trim()
  }

  const getPatientHnCreatedAt = (patient) => {
    return [
      objectPath.get(patient, 'hnCreatedAt'),
      objectPath.get(patient, 'patient.createdAt'),
      objectPath.get(patient, 'Patient.createdAt'),
      objectPath.get(patient, 'createdAt'),
    ].find((value) => value !== undefined && value !== null && value !== '')
  }

  const comparePatientsNewestFirst = (left, right) => {
    const leftCreatedAt = new Date(getPatientHnCreatedAt(left) || 0).getTime()
    const rightCreatedAt = new Date(getPatientHnCreatedAt(right) || 0).getTime()
    const leftTimestamp = Number.isNaN(leftCreatedAt) ? 0 : leftCreatedAt
    const rightTimestamp = Number.isNaN(rightCreatedAt) ? 0 : rightCreatedAt
    if (leftTimestamp !== rightTimestamp) return rightTimestamp - leftTimestamp

    return String(objectPath.get(right, 'hn') || '').localeCompare(
      String(objectPath.get(left, 'hn') || ''),
      undefined,
      { numeric: true },
    )
  }

  const searchHN = async (cid, data) => {
    setIsSearchingHN(true)
    setHasSearchedHN(false)
    patientDetailCacheRef.current = {}
    let patient = []
    let searchMatchType = 'none'
    let searchedCid = cid || ''
    if (cid) {
      patient = await getPatientList(
        `/pas/patient/search?cId=${encodeURIComponent(
          cid,
        )}&include=person&page=1&perPage=10`,
      )
      if (patient.length > 0) searchMatchType = 'citizenId'
    }
    if (patient.length === 0) {
      let fullName = getCardFullName(data)
      if (fullName) {
        patient = await getPatientList(
          `/pas/patient/search?fullName=${encodeURIComponent(
            fullName,
          )}&include=person&page=1&perPage=10`,
        )
        if (patient.length > 0) searchMatchType = 'fullName'
      }
    }
    patient = patient.map((item) => ({
      ...mergePatientPersonData(item),
      hnCreatedAt: getPatientHnCreatedAt(item),
    }))
    patient.sort(comparePatientsNewestFirst)
    let selected = searchMatchType === 'fullName' ? null : patient[0] || null
    setHnSearchMatchType(searchMatchType)
    hnSearchMatchTypeRef.current = searchMatchType
    setSearchedCitizenId(searchedCid)
    setPatientList(patient)
    setSelectedPatient(selected)
    setIsSearchingHN(false)
    setHasSearchedHN(true)
    setMessage('อ่านบัตรประชาชนเรียบร้อย')
    return selected
  }

  const getFirstValue = (...values) => {
    return values.find(
      (value) => value !== undefined && value !== null && value !== '',
    )
  }

  const normalizeLookupText = (value) =>
    String(value || '')
      .trim()
      .toLowerCase()

  const getLookupValue = (item = {}, keys = []) => {
    return keys.map((key) => item[key]).find(
      (value) => value !== undefined && value !== null && value !== '',
    )
  }

  const getPatientPersonData = (patient = {}) => {
    let person =
      getFirstValue(
        objectPath.get(patient, 'person'),
        objectPath.get(patient, 'Person'),
        objectPath.get(patient, 'patient.person'),
        objectPath.get(patient, 'patient.Person'),
      ) || {}
    if (Array.isArray(person)) person = person[0] || {}

    return {
      ...patient,
      ...person,
      hn: getFirstValue(objectPath.get(patient, 'hn'), person.hn),
    }
  }

  const getPatientProfileData = (patient = {}, person = {}) => {
    return mergeNonEmptySmartCardData(getPatientPersonData(patient), person)
  }

  const getProfileWithImageFile = (profile = {}, imageFile) => {
    if (imageFile === undefined) return profile

    return {
      ...profile,
      attachment: imageFile,
      imageFile,
      imageAttachment: {
        ...objectPath.get(profile, 'imageAttachment'),
        imageFile,
      },
      patientAttachement: {
        ...objectPath.get(profile, 'patientAttachement'),
        imageFile,
      },
      patientImage: imageFile,
    }
  }

  const mergePatientPersonData = (
    patient = {},
    person = {},
    citizenAddress,
  ) => {
    let patientProfile = getPatientProfileData(patient, person)
    let patientData = {
      ...patient,
      ...patientProfile,
      patientProfile,
    }
    if (citizenAddress) {
      patientData.citizenAddress = citizenAddress
    }
    return patientData
  }

  const getHydratedPatient = async (patient = {}) => {
    let hn = objectPath.get(patient, 'hn')
    if (!hn) return mergePatientPersonData(patient)

    let cacheKey = String(hn)
    if (patientDetailCacheRef.current[cacheKey]) {
      return {
        ...patient,
        ...patientDetailCacheRef.current[cacheKey],
        hnCreatedAt: getPatientHnCreatedAt(patient),
      }
    }

    let [person, citizenAddress] = await Promise.all([
      getPerson(hn),
      getPersonAddress(hn),
    ])
    let hydratedPatient = {
      ...mergePatientPersonData(patient, person, citizenAddress),
      hnCreatedAt: getPatientHnCreatedAt(patient),
    }
    patientDetailCacheRef.current[cacheKey] = hydratedPatient
    return hydratedPatient
  }

  const parseBirthDate = (value) => {
    if (!value) return null
    let text = String(value).trim()
    if (/^\d{8}$/.test(text)) {
      let year = Number(text.slice(0, 4))
      let month = Number(text.slice(4, 6))
      let day = Number(text.slice(6, 8))
      if (year > 2400) year -= 543
      let date = new Date(year, month - 1, day)
      return Number.isNaN(date.getTime()) ? null : date
    }
    if (/^\d{4}-\d{2}-\d{2}/.test(text)) {
      let [year, month, day] = text.slice(0, 10).split('-').map(Number)
      if (year > 2400) year -= 543
      let date = new Date(year, month - 1, day)
      return Number.isNaN(date.getTime()) ? null : date
    }
    let date = new Date(text)
    return Number.isNaN(date.getTime()) ? null : date
  }

  const formatDisplayDate = (value) => {
    let date = parseBirthDate(value)
    if (!date) return value || '-'
    return formatDate(date) || '-'
  }

  const formatHnCreatedAt = (patient) => {
    let value = getPatientHnCreatedAt(patient)
    if (!value) return '-'
    let date = new Date(value)
    if (Number.isNaN(date.getTime())) return '-'
    return formatDateTime(date) || '-'
  }

  const getAgeText = (value) => {
    let date = parseBirthDate(value)
    if (!date) return '-'
    let today = new Date()
    let age = today.getFullYear() - date.getFullYear()
    let hasNotBirthday =
      today.getMonth() < date.getMonth() ||
      (today.getMonth() === date.getMonth() &&
        today.getDate() < date.getDate())
    if (hasNotBirthday) age -= 1
    return age >= 0 ? `${age} ปี` : '-'
  }

  const getPatientCitizenId = (patient) => {
    return (
      getFirstValue(
        objectPath.get(patient, 'person.citizenId'),
        objectPath.get(patient, 'Person.citizenId'),
        objectPath.get(patient, 'citizenId'),
        objectPath.get(patient, 'cId'),
        objectPath.get(patient, 'cid'),
      ) || '-'
    )
  }

  const normalizeCitizenIdValue = (value) =>
    String(value || '').replace(/\D/g, '')

  const isPatientCitizenIdMatchedCard = (patient) => {
    let patientCitizenId = normalizeCitizenIdValue(getPatientCitizenId(patient))
    let cardCitizenId = normalizeCitizenIdValue(searchedCitizenId || cardData.cid)
    return Boolean(patientCitizenId && cardCitizenId && patientCitizenId === cardCitizenId)
  }

  const getPatientBirthDate = (patient) => {
    return getFirstValue(
      objectPath.get(patient, 'person.birthDate'),
      objectPath.get(patient, 'person.birthdate'),
      objectPath.get(patient, 'Person.birthDate'),
      objectPath.get(patient, 'Person.birthdate'),
      objectPath.get(patient, 'birthDate'),
      objectPath.get(patient, 'birthdate'),
    )
  }

  const getPatientCardStatus = (patient) => {
    let isFakeCitizenId = getFirstValue(
      objectPath.get(patient, 'person.isFakeCitizenId'),
      objectPath.get(patient, 'person.0.isFakeCitizenId'),
      objectPath.get(patient, 'Person.isFakeCitizenId'),
      objectPath.get(patient, 'Person.0.isFakeCitizenId'),
      objectPath.get(patient, 'patient.person.isFakeCitizenId'),
      objectPath.get(patient, 'patient.person.0.isFakeCitizenId'),
      objectPath.get(patient, 'isFakeCitizenId'),
    )
    if (isFakeCitizenId === true || String(isFakeCitizenId) === '1') {
      return 'Fake Id'
    }
    return 'Real Id'
  }

  const isSelectedPatient = (patient) => {
    let hn = objectPath.get(patient, 'hn')
    return (
      selectedPatient === patient ||
      (selectedPatientHn && String(selectedPatientHn) === String(hn))
    )
  }

  const handleCreateNewHn = () => {
    setSelectedPatient(null)
    if (!onUpdateDesignerData) return
    let smartCardData =
      objectPath.get(designerData, 'smartCardData') ||
      objectPath.get(designerData, designerKey + '.smartCardData') ||
      {}
    let cardProfile = getCardProfile(smartCardData)
    let cardCitizenAddress = getCardCitizenAddress(smartCardData)
    let imageFile = getSmartCardImageFile(smartCardData)
    let citizenId = getFirstValue(
      objectPath.get(smartCardData, 'citizenId'),
      objectPath.get(designerData, 'citizenId'),
      cardData.cid,
    )
    let patient = null
    let patientProfile = {}
    let hn = undefined
    let citizenAddress = mergeCitizenAddressData(null, cardCitizenAddress)
    let profile = getProfileWithImageFile(
      mergePersonWithSmartCardProfile(patientProfile, cardProfile, hn),
      imageFile,
    )
    let smartCardAction = 'createNewHn'
    objectPath.set(smartCardData, 'hn', hn)
    objectPath.set(smartCardData, 'patient', patient)
    objectPath.set(smartCardData, 'patientProfile', patientProfile)
    objectPath.set(smartCardData, 'profile', profile)
    objectPath.set(smartCardData, 'cardProfile', cardProfile)
    objectPath.set(smartCardData, 'citizenAddress', citizenAddress)
    objectPath.set(smartCardData, 'cardCitizenAddress', cardCitizenAddress)
    objectPath.set(smartCardData, 'attachment', imageFile)
    objectPath.set(smartCardData, 'citizenId', citizenId)
    objectPath.set(smartCardData, 'smartCardAction', smartCardAction)
    objectPath.set(smartCardData, 'hnSearchMatchType', hnSearchMatchTypeRef.current)
    setSmartCardDesignerValue('profile', profile)
    setSmartCardDesignerValue('cardProfile', cardProfile)
    setSmartCardDesignerValue('citizenAddress', citizenAddress)
    setSmartCardDesignerValue('cardCitizenAddress', cardCitizenAddress)
    setSmartCardImageDesignerData(imageFile)
    setSmartCardDesignerValue('citizenId', citizenId)
    setSmartCardDesignerValue('hn', hn)
    setSmartCardDesignerValue('patient', patient)
    setSmartCardDesignerValue('patientProfile', patientProfile)
    setSmartCardDesignerValue('smartCardData', smartCardData)
    setSmartCardDesignerValue('smartCardAction', smartCardAction)
    setSmartCardDesignerValue('hnSearchMatchType', hnSearchMatchTypeRef.current)
    objectPath.set(designerData, designerKey + '.smartCardData', smartCardData)
    objectPath.set(designerData, 'smartCardData', smartCardData)
    if (onUpdateData) {
      onUpdateData(
        getSmartCardUpdateData({
          profile,
          cardProfile,
          citizenAddress,
          cardCitizenAddress,
          imageFile,
          citizenId,
          hn,
          patient,
          patientProfile,
          smartCardData,
          smartCardAction,
          hnSearchMatchType: hnSearchMatchTypeRef.current,
        }),
      )
    }
    onUpdateDesignerData(designerData)
  }

  const handleSelectPatient = (patient) => {
    setSelectedPatient(patient)
    let hn = objectPath.get(patient, 'hn')
    let patientProfile =
      objectPath.get(patient, 'patientProfile') || getPatientPersonData(patient)
    if (onUpdateDesignerData) {
      objectPath.set(designerData, `${designerKey}.hn`, hn)
      objectPath.set(designerData, 'hn', hn)
      objectPath.set(designerData, '@sendData.hn', hn)
      objectPath.set(designerData, `@sendData.${designerKey}.hn`, hn)
      let smartCardData =
        objectPath.get(designerData, 'smartCardData') ||
        objectPath.get(designerData, `${designerKey}.smartCardData`)
      if (smartCardData) {
        let cardProfile = getCardProfile(smartCardData)
        let cardCitizenAddress = getCardCitizenAddress(smartCardData)
        let citizenAddress = mergeCitizenAddressData(
          objectPath.get(patient, 'citizenAddress'),
          cardCitizenAddress,
        )
        let imageFile = getSmartCardImageFile(smartCardData)
        let smartCardAction = 'existingHn'
        let profile = getProfileWithImageFile(
          mergePersonWithSmartCardProfile(
            patientProfile,
            cardProfile,
            hn,
          ),
          imageFile,
        )
        objectPath.set(smartCardData, 'hn', hn)
        objectPath.set(smartCardData, 'patient', patient)
        objectPath.set(smartCardData, 'patientProfile', patientProfile)
        objectPath.set(smartCardData, 'profile', profile)
        objectPath.set(smartCardData, 'cardProfile', cardProfile)
        objectPath.set(smartCardData, 'citizenAddress', citizenAddress)
        objectPath.set(smartCardData, 'cardCitizenAddress', cardCitizenAddress)
        objectPath.set(smartCardData, 'attachment', imageFile)
        objectPath.set(smartCardData, 'smartCardAction', smartCardAction)
        objectPath.set(smartCardData, 'hnSearchMatchType', hnSearchMatchTypeRef.current)
        setSmartCardDesignerValue('profile', profile)
        setSmartCardDesignerValue('cardProfile', cardProfile)
        setSmartCardDesignerValue('citizenAddress', citizenAddress)
        setSmartCardDesignerValue('cardCitizenAddress', cardCitizenAddress)
        setSmartCardImageDesignerData(imageFile)
        setSmartCardDesignerValue('smartCardAction', smartCardAction)
        setSmartCardDesignerValue('hnSearchMatchType', hnSearchMatchTypeRef.current)
        objectPath.set(
          designerData,
          `${designerKey}.smartCardData`,
          smartCardData,
        )
        objectPath.set(designerData, 'smartCardData', smartCardData)
        objectPath.set(designerData, `${designerKey}.patient`, patient)
        objectPath.set(designerData, 'patient', patient)
        objectPath.set(
          designerData,
          `${designerKey}.patientProfile`,
          patientProfile,
        )
        objectPath.set(designerData, 'patientProfile', patientProfile)
        objectPath.set(designerData, '@sendData.smartCardData', smartCardData)
        objectPath.set(
          designerData,
          `@sendData.${designerKey}.smartCardData`,
          smartCardData,
        )
        objectPath.set(designerData, '@sendData.patient', patient)
        objectPath.set(designerData, '@sendData.patientProfile', patientProfile)
        objectPath.set(
          designerData,
          `@sendData.${designerKey}.patient`,
          patient,
        )
        objectPath.set(
          designerData,
          `@sendData.${designerKey}.patientProfile`,
          patientProfile,
        )
        if (onUpdateData) {
          onUpdateData(
            getSmartCardUpdateData({
              profile,
              cardProfile,
              citizenAddress,
              cardCitizenAddress,
              imageFile,
              citizenId: objectPath.get(smartCardData, 'citizenId'),
              hn,
              patient,
              patientProfile,
              smartCardData,
              smartCardAction,
              hnSearchMatchType: hnSearchMatchTypeRef.current,
            }),
          )
        }
      }
      onUpdateDesignerData(designerData)
    }
  }

  const setSmartCardDesignerValue = (key, value) => {
    objectPath.set(designerData, `${designerKey}.${key}`, value)
    objectPath.set(designerData, key, value)
    objectPath.set(designerData, `@sendData.${key}`, value)
    objectPath.set(designerData, `@sendData.${designerKey}.${key}`, value)
  }

  const getSmartCardImageFile = (smartCardData = {}) => {
    return getFirstValue(
      objectPath.get(smartCardData, 'imageFile'),
      objectPath.get(designerData, `${designerKey}.imageFile`),
      objectPath.get(designerData, 'imageFile'),
      objectPath.get(designerData, `${designerKey}.attachment`),
      objectPath.get(designerData, 'attachment'),
      objectPath.get(designerData, 'imageAttachment.imageFile'),
      objectPath.get(designerData, 'patientAttachement.imageFile'),
      objectPath.get(designerData, 'patientImage'),
      objectPath.get(designerData, '@sendData.imageFile'),
      objectPath.get(designerData, '@sendData.attachment'),
      objectPath.get(designerData, '@sendData.imageAttachment.imageFile'),
      objectPath.get(designerData, '@sendData.patientAttachement.imageFile'),
      objectPath.get(designerData, '@sendData.patientImage'),
    )
  }

  const setSmartCardImageDesignerData = (imageFile) => {
    if (imageFile === undefined) return

    setSmartCardDesignerValue('imageFile', imageFile)
    setSmartCardDesignerValue('attachment', imageFile)
    objectPath.set(designerData, 'imageAttachment.imageFile', imageFile)
    objectPath.set(designerData, 'patientAttachement.imageFile', imageFile)
    objectPath.set(designerData, 'patientImage', imageFile)
    objectPath.set(
      designerData,
      '@sendData.imageAttachment.imageFile',
      imageFile,
    )
    objectPath.set(
      designerData,
      '@sendData.patientAttachement.imageFile',
      imageFile,
    )
    objectPath.set(designerData, '@sendData.patientImage', imageFile)
  }

  const getSmartCardUpdateData = ({
    profile,
    cardProfile,
    citizenAddress,
    cardCitizenAddress,
    imageFile,
    citizenId,
    hn,
    patient,
    patientProfile,
    smartCardData,
    smartCardAction,
    hnSearchMatchType,
  }) => {
    let resolvedSmartCardAction =
      smartCardAction || objectPath.get(smartCardData, 'smartCardAction')
    let resolvedHnSearchMatchType =
      hnSearchMatchType || objectPath.get(smartCardData, 'hnSearchMatchType')
    return {
      profile,
      cardProfile,
      citizenAddress,
      cardCitizenAddress,
      imageFile,
      attachment: imageFile,
      imageAttachment: { imageFile },
      patientAttachement: { imageFile },
      patientImage: imageFile,
      citizenId,
      hn,
      patient,
      patientProfile,
      smartCardData,
      smartCardAction: resolvedSmartCardAction,
      hnSearchMatchType: resolvedHnSearchMatchType,
      closeCallback: objectPath.get(designerData, 'closeCallback'),
    }
  }

  const getDesignerHN = () => {
    return getFirstValue(
      objectPath.get(designerData, `${designerKey}.hn`),
      objectPath.get(designerData, 'hn'),
      objectPath.get(designerData, `@sendData.${designerKey}.hn`),
      objectPath.get(designerData, '@sendData.hn'),
      objectPath.get(designerData, `${designerKey}.smartCardData.hn`),
      objectPath.get(designerData, 'smartCardData.hn'),
    )
  }

  const getPerson = async (hn) => {
    if (!hn) return null
    let person = await httpClient
      .get(`/pas/person/hn/${encodeURIComponent(hn)}`)
      .then(({ data }) => data.data)
      .catch(() => null)
    return person
  }

  const getPatientAddress = async (hn) => {
    if (!hn) return null
    let patient = await httpClient
      .get(`/pas/patient/${encodeURIComponent(hn)}?include=personAddress`)
      .then(({ data }) => data.data || data)
      .catch(() => null)
    return patient
  }

  const getPersonAddress = async (hn) => {
    if (!hn) return null
    let [patientAddress, personAddress] = await Promise.all([
      getPatientAddress(hn),
      httpClient
        .get(
          `/pas/person-address/hn/${encodeURIComponent(
            hn,
          )}?addressTypeCode=CITIZEN&include=addressCountry,addressDistrict,addressProvince,addressSubdistrict`,
        )
        .then(({ data }) => data.data || data)
        .catch(() => null),
    ])
    return mergeNonEmptySmartCardData(
      normalizeAddressData(patientAddress),
      normalizeAddressData(personAddress),
    )
  }

  const normalizeAddressData = (address) => {
    return normalizeSmartCardAddressData(address)
  }

  const getCardProfile = (smartCardData) => {
    return (
      objectPath.get(smartCardData, 'cardProfile') ||
      objectPath.get(designerData, `${designerKey}.cardProfile`) ||
      objectPath.get(designerData, 'cardProfile') ||
      objectPath.get(smartCardData, 'profile') ||
      objectPath.get(designerData, `${designerKey}.profile`) ||
      objectPath.get(designerData, 'profile') ||
      {}
    )
  }

  const getCardCitizenAddress = (smartCardData) => {
    return (
      objectPath.get(smartCardData, 'cardCitizenAddress') ||
      objectPath.get(designerData, `${designerKey}.cardCitizenAddress`) ||
      objectPath.get(designerData, 'cardCitizenAddress') ||
      objectPath.get(smartCardData, 'citizenAddress') ||
      objectPath.get(designerData, `${designerKey}.citizenAddress`) ||
      objectPath.get(designerData, 'citizenAddress') ||
      {}
    )
  }

  const getRawCardCitizenAddress = (data = {}) => {
    return mergeNonEmptySmartCardData({
      address: data.address,
      fullAddress: data.address,
      array_address: data.array_address,
    })
  }

  const isStructuredAddressData = (address = {}) => {
    let addressData = normalizeAddressData(address)
    return [
      'houseNumber',
      'villageCode',
      'roadName',
      'subDistrictCode',
      'districtCode',
      'provinceCode',
      'postalCode',
      'countryCode',
    ].some((key) => addressData[key])
  }

  const mergeCitizenAddressData = (personAddress, cardCitizenAddress) => {
    return mergeNonEmptySmartCardData(
      normalizeAddressData(personAddress),
      normalizeAddressData(cardCitizenAddress),
    )
  }

  const getSelectedPatientProfile = (smartCardData = {}) => {
    let patientProfile = getFirstValue(
      objectPath.get(smartCardData, 'patientProfile'),
      objectPath.get(designerData, `${designerKey}.patientProfile`),
      objectPath.get(designerData, 'patientProfile'),
      objectPath.get(designerData, '@sendData.patientProfile'),
      objectPath.get(designerData, `@sendData.${designerKey}.patientProfile`),
    )
    if (patientProfile) return patientProfile

    let patient = getFirstValue(
      objectPath.get(smartCardData, 'patient'),
      objectPath.get(designerData, `${designerKey}.patient`),
      objectPath.get(designerData, 'patient'),
      objectPath.get(designerData, '@sendData.patient'),
      objectPath.get(designerData, `@sendData.${designerKey}.patient`),
      selectedPatient,
    )

    return getPatientPersonData(patient)
  }

  const updateCitizenAddressDesignerData = (personAddress) => {
    let hn = getDesignerHN()
    let smartCardData =
      objectPath.get(designerData, 'smartCardData') ||
      objectPath.get(designerData, `${designerKey}.smartCardData`) ||
      {}
    let cardCitizenAddress = getCardCitizenAddress(smartCardData)
    let citizenAddress = mergeCitizenAddressData(
      personAddress,
      cardCitizenAddress,
    )
    if (Object.keys(citizenAddress).length === 0) return

    setSmartCardDesignerValue('citizenAddress', citizenAddress)
    setSmartCardDesignerValue('cardCitizenAddress', cardCitizenAddress)
    objectPath.set(smartCardData, 'hn', hn)
    objectPath.set(smartCardData, 'citizenAddress', citizenAddress)
    objectPath.set(smartCardData, 'cardCitizenAddress', cardCitizenAddress)
    setSmartCardDesignerValue('smartCardData', smartCardData)
    let updateData = getSmartCardUpdateData({
      profile: objectPath.get(smartCardData, 'profile'),
      cardProfile: objectPath.get(smartCardData, 'cardProfile'),
      citizenAddress,
      cardCitizenAddress,
      imageFile: getSmartCardImageFile(smartCardData),
      citizenId: objectPath.get(smartCardData, 'citizenId'),
      hn,
      patient: objectPath.get(smartCardData, 'patient'),
      patientProfile: objectPath.get(smartCardData, 'patientProfile'),
      smartCardData,
    })
    if (onUpdateData) {
      onUpdateData(updateData)
    }
    if (onUpdateDesignerData) {
      onUpdateDesignerData(designerData)
    }
    return updateData
  }

  const updateTransformedCitizenAddressDesignerData = async (personAddress) => {
    let hn = getDesignerHN()
    let smartCardData =
      objectPath.get(designerData, 'smartCardData') ||
      objectPath.get(designerData, `${designerKey}.smartCardData`) ||
      {}
    let currentCardCitizenAddress = getCardCitizenAddress(smartCardData)
    let rawCardData = getFirstValue(
      objectPath.get(smartCardData, 'rawCardData'),
      objectPath.get(designerData, `${designerKey}.rawCardData`),
      objectPath.get(designerData, 'rawCardData'),
      cardData,
    )
    let cardCitizenAddress = currentCardCitizenAddress
    if (!isStructuredAddressData(cardCitizenAddress)) {
      let transformedAddress = await convertAddress(rawCardData)
      if (Object.keys(normalizeAddressData(transformedAddress)).length > 0) {
        cardCitizenAddress = transformedAddress
      }
    }
    let citizenAddress = mergeCitizenAddressData(
      personAddress,
      cardCitizenAddress,
    )
    if (Object.keys(citizenAddress).length === 0) return

    setSmartCardDesignerValue('citizenAddress', citizenAddress)
    setSmartCardDesignerValue('cardCitizenAddress', cardCitizenAddress)
    objectPath.set(smartCardData, 'hn', hn)
    objectPath.set(smartCardData, 'citizenAddress', citizenAddress)
    objectPath.set(smartCardData, 'cardCitizenAddress', cardCitizenAddress)
    setSmartCardDesignerValue('smartCardData', smartCardData)
    let updateData = getSmartCardUpdateData({
      profile: objectPath.get(smartCardData, 'profile'),
      cardProfile: objectPath.get(smartCardData, 'cardProfile'),
      citizenAddress,
      cardCitizenAddress,
      imageFile: getSmartCardImageFile(smartCardData),
      citizenId: objectPath.get(smartCardData, 'citizenId'),
      hn,
      patient: objectPath.get(smartCardData, 'patient'),
      patientProfile: objectPath.get(smartCardData, 'patientProfile'),
      smartCardData,
      smartCardAction: objectPath.get(smartCardData, 'smartCardAction'),
      hnSearchMatchType: objectPath.get(smartCardData, 'hnSearchMatchType'),
    })
    if (onUpdateData) {
      onUpdateData(updateData)
    }
    if (onUpdateDesignerData) {
      onUpdateDesignerData(designerData)
    }
    return updateData
  }

  const updateProfileDesignerData = (person) => {
    let hn = getDesignerHN()
    let smartCardData =
      objectPath.get(designerData, 'smartCardData') ||
      objectPath.get(designerData, `${designerKey}.smartCardData`) ||
      {}
    let patientProfile = getSelectedPatientProfile(smartCardData)
    let profileData = mergeNonEmptySmartCardData(patientProfile, person)
    if (Object.keys(profileData).length === 0) return
    let cardProfile = getCardProfile(smartCardData)
    let imageFile = getSmartCardImageFile(smartCardData)
    let profile = getProfileWithImageFile(
      mergePersonWithSmartCardProfile(profileData, cardProfile, hn),
      imageFile,
    )
    setSmartCardDesignerValue('profile', profile)
    setSmartCardDesignerValue('cardProfile', cardProfile)
    setSmartCardImageDesignerData(imageFile)
    objectPath.set(smartCardData, 'hn', hn)
    objectPath.set(smartCardData, 'patientProfile', patientProfile)
    objectPath.set(smartCardData, 'profile', profile)
    objectPath.set(smartCardData, 'cardProfile', cardProfile)
    if (imageFile !== undefined) {
      objectPath.set(smartCardData, 'imageFile', imageFile)
      objectPath.set(smartCardData, 'attachment', imageFile)
    }
    setSmartCardDesignerValue('patientProfile', patientProfile)
    setSmartCardDesignerValue('smartCardData', smartCardData)
    let updateData = getSmartCardUpdateData({
      profile,
      cardProfile,
      citizenAddress: objectPath.get(smartCardData, 'citizenAddress'),
      cardCitizenAddress: objectPath.get(
        smartCardData,
        'cardCitizenAddress',
      ),
      imageFile,
      citizenId: objectPath.get(smartCardData, 'citizenId'),
      hn,
      patient: objectPath.get(smartCardData, 'patient'),
      patientProfile,
      smartCardData,
    })
    if (onUpdateData) {
      onUpdateData(updateData)
    }
    if (onUpdateDesignerData) {
      onUpdateDesignerData(designerData)
    }
    return updateData
  }

  const convertReligionCode = (religionName, religionCode) => {
    let normalizedReligionName = normalizeLookupText(religionName)
    let religion = (religionList || []).find((item) => {
      let label = getLookupValue(item, [
        'religionName',
        'name',
        'description',
      ])
      return normalizeLookupText(label) === normalizedReligionName
    })
    return getFirstValue(
      getLookupValue(religion, ['religionCode', 'code', 'id']),
      religionCode,
    )
  }

  const convertProfile = async (data) => {
    let profile = {
      prefixName: data.th.prefix_name,
      firstName: data.th.first_name,
      lastName: data.th.last_name,
      middleName: data.th.middle_name,
      prefixNameEn: data.en.prefix_name,
      firstNameEn: data.en.first_name,
      lastNameEn: data.en.last_name,
      middleNameEn: data.en.middle_name,
      genderId: data.gender_ind,
      religionCode: convertReligionCode(data.th.religion, data.religion_ind),
      citizenId: data.cid,
      birthDate: data.birth_date,
      cardExpiredDate: data.expire_date,
    }
    console.log('convertProfile', profile)
    return profile
  }

  const convertHouseNumber = (citizenAddress, value) => {
    if (citizenAddress.houseNumber) return citizenAddress
    if (/^\d/.test(value)) {
      citizenAddress.houseNumber = value
    }
    return citizenAddress
  }

  const convertVillageCode = (citizenAddress, value) => {
    if (citizenAddress.villageCode) return citizenAddress
    if (/^(หมู่)/.test(value)) {
      citizenAddress.villageCode = value
    }
    return citizenAddress
  }

  const convertRoadName = (citizenAddress, value) => {
    if (citizenAddress.roadName) return citizenAddress
    if (/^(ถนน|ตรอก|ซอย)/.test(value)) {
      citizenAddress.roadName = value
    }
    return citizenAddress
  }

  const convertProvinceCode = (citizenAddress, value) => {
    if (citizenAddress.provinceCode) return citizenAddress
    if (/^(\d|ถนน|ตรอก|ซอย|ตำบล|อำเภอ|แขวง|เขต)/.test(value) === false) {
      citizenAddress.provinceCode = value
    }
    return citizenAddress
  }

  const convertDistrictCode = (citizenAddress, value) => {
    if (citizenAddress.districtCode) return citizenAddress
    if (/^(อำเภอ|เขต)/.test(value)) {
      citizenAddress.districtCode = value
    }
    return citizenAddress
  }

  const convertSubDistrictCode = (citizenAddress, value) => {
    if (citizenAddress.subDistrictCode) return citizenAddress
    if (/^(ตำบล|แขวง)/.test(value)) {
      citizenAddress.subDistrictCode = value
    }
    return citizenAddress
  }

  const escapeRegex = (str) => {
    return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  }

  const findFromArray = (arr, keyword, code) => {
    return arr.find((item) => {
      const regex = new RegExp(escapeRegex(item.name), 'i')
      return regex.test(keyword) || item[code]
    })
  }

  const mapSubDistrict = async (citizenAddress) => {
    let { provinceCode, districtCode, subDistrictCode } = citizenAddress
    let subDistrictList = await httpClient
      .get(
        `/core/address-subdistrict?provinceCode=${provinceCode}&districtCode=${districtCode}&page=1&perPage=1000`,
      )
      .then(({ data }) => data.data)
      .catch(() => [])
    let subDistrict = findFromArray(
      subDistrictList,
      subDistrictCode,
      'subDistrictCode',
    )
    if (subDistrict) {
      citizenAddress.subDistrictCode = subDistrict.subDistrictCode
    } else {
      citizenAddress.subDistrictCode = null
    }
    return await citizenAddress
  }

  const mapDistrict = async (citizenAddress) => {
    let { provinceCode, districtCode } = citizenAddress
    let districtList = await httpClient
      .get(
        `/core/address-district?provinceCode=${provinceCode}&page=1&perPage=1000`,
      )
      .then(({ data }) => data.data)
      .catch(() => [])
    let district = findFromArray(districtList, districtCode, 'districtCode')
    if (district) {
      citizenAddress.districtCode = district.districtCode
    } else {
      citizenAddress.districtCode = null
    }
    return await citizenAddress
  }

  const mapProvinceCode = async (citizenAddress) => {
    let { provinceCode } = citizenAddress
    let provinceList = await httpClient
      .get('/core/address-province')
      .then(({ data }) => data.data)
      .catch(() => [])
    let province = findFromArray(provinceList, provinceCode, 'provinceCode')
    if (province) {
      citizenAddress.provinceCode = province.provinceCode
    } else {
      citizenAddress.provinceCode = null
    }
    return await citizenAddress
  }

  const convertCountryCode = async (cid) => {
    if (cid) {
      let countryList = await httpClient
        .get('/core/address-country?page=1&perPage=1000')
        .then(({ data }) => data.data)
        .catch(() => [])
      let country = countryList.find(({ name }) => name === 'ไทย')
      return country?.countryCode ?? '00'
    }
    return '00'
  }

  const mapAddress = async (payload) => {
    let address = await httpClient
      .post('/core/utils/id-card-transform', payload)
      .then(({ data }) => data.data)
      .catch(() => ({}))
    return address
  }

  const getAddressRecords = async (path) => {
    return httpClient
      .get(path)
      .then(({ data }) => {
        let records = data?.data || data
        if (Array.isArray(records)) return records
        if (Array.isArray(records?.data)) return records.data
        if (Array.isArray(records?.items)) return records.items
        return []
      })
      .catch(() => [])
  }

  const reconcileSmartCardAddress = async (data, address = {}) => {
    let addressText = getSmartCardAddressText(data)
    let reconciledAddress = mergeNonEmptySmartCardData(address, {
      address: data.address,
      fullAddress: data.address,
      array_address: data.array_address,
      houseNumber: getSmartCardHouseNumber(data),
      villageCode: getSmartCardVillageCode(data),
    })
    if (!addressText) return reconciledAddress

    let provinceList = await getAddressRecords(
      '/core/address-province?page=1&perPage=1000',
    )
    let province = findSmartCardAddressRecord(
      provinceList,
      addressText,
      ['', 'จังหวัด', 'จ.'],
    )
    if (!province?.provinceCode) return reconciledAddress

    reconciledAddress.provinceCode = province.provinceCode
    reconciledAddress.provinceName = getSmartCardAddressRecordName(province)
    reconciledAddress.addressProvince = province

    let districtList = await getAddressRecords(
      `/core/address-district?provinceCode=${encodeURIComponent(
        province.provinceCode,
      )}&page=1&perPage=1000`,
    )
    let district = findSmartCardAddressRecord(
      districtList,
      addressText,
      ['อำเภอ', 'อ.', 'เขต'],
    )
    if (!district?.districtCode) {
      reconciledAddress.districtCode = ''
      reconciledAddress.subDistrictCode = ''
      delete reconciledAddress.districtName
      delete reconciledAddress.subDistrictName
      delete reconciledAddress.addressDistrict
      delete reconciledAddress.addressSubdistrict
      return reconciledAddress
    }

    reconciledAddress.districtCode = district.districtCode
    reconciledAddress.districtName = getSmartCardAddressRecordName(district)
    reconciledAddress.addressDistrict = district

    let subDistrictList = await getAddressRecords(
      `/core/address-subdistrict?provinceCode=${encodeURIComponent(
        province.provinceCode,
      )}&districtCode=${encodeURIComponent(
        district.districtCode,
      )}&page=1&perPage=1000`,
    )
    let subDistrict = findSmartCardAddressRecord(
      subDistrictList,
      addressText,
      ['ตำบล', 'ต.', 'แขวง'],
    )
    if (!subDistrict?.subDistrictCode) {
      reconciledAddress.subDistrictCode = ''
      delete reconciledAddress.subDistrictName
      delete reconciledAddress.addressSubdistrict
      return reconciledAddress
    }

    reconciledAddress.subDistrictCode = subDistrict.subDistrictCode
    reconciledAddress.subDistrictName = getSmartCardAddressRecordName(
      subDistrict,
    )
    reconciledAddress.addressSubdistrict = subDistrict
    reconciledAddress.postalCode =
      subDistrict.postalCode ||
      subDistrict.zipCode ||
      reconciledAddress.postalCode

    return reconciledAddress
  }

  const getAddressTransformKey = (data = {}) => {
    return JSON.stringify({
      array_address: data.array_address || [],
      address: data.address || '',
    })
  }

  const convertAddress = async (data = {}) => {
    if (!data?.array_address && !data?.address) return {}
    let cacheKey = getAddressTransformKey(data)
    if (addressTransformCacheRef.current[cacheKey]) {
      return addressTransformCacheRef.current[cacheKey]
    }
    let { array_address } = data
    let citizenAddress = await mapAddress({ array_address })
    let normalizedAddress = await reconcileSmartCardAddress(
      data,
      normalizeAddressData(citizenAddress),
    )
    addressTransformCacheRef.current[cacheKey] = normalizedAddress
    console.log('convertAddress', normalizedAddress)
    return normalizedAddress
  }

  const convertImageFile = (data) => {
    let { photo } = data
    if (photo) {
      var arr = photo.split(','),
        mime = arr[0].match(/:(.*?);/)[1],
        bstr = atob(arr[1]),
        n = bstr.length,
        u8arr = new Uint8Array(n)
      while (n--) {
        u8arr[n] = bstr.charCodeAt(n)
      }
      let imageFile = new File([u8arr], 'profile.jpg', { type: mime })
      return [imageFile]
    }
    return undefined
  }

  const convertCardData = async (data) => {
    let { issuer, cid, issue_date } = data
    if (!issuer || !cid || issue_date === 'Invalid Date') return
    if (onUpdateDesignerData) {
      setPatientList([])
      setSelectedPatient(null)
      setIsSearchingHN(false)
      setHasSearchedHN(false)
      let cardProfile = await convertProfile(data)
      let cardCitizenAddress = getRawCardCitizenAddress(data)
      let imageFile = convertImageFile(data)
      let patient = await searchHN(cid, data)
      let searchMatchType = hnSearchMatchTypeRef.current
      let hn = patient ? objectPath.get(patient, 'hn') : undefined
      let patientProfile = patient
        ? objectPath.get(patient, 'patientProfile') || getPatientPersonData(patient)
        : {}
      let citizenAddress = mergeCitizenAddressData(
        patient ? objectPath.get(patient, 'citizenAddress') : null,
        cardCitizenAddress,
      )
      let smartCardAction = hn ? 'existingHn' : 'createNewHn'
      let profile = getProfileWithImageFile(
        mergePersonWithSmartCardProfile(patientProfile, cardProfile, hn),
        imageFile,
      )
      let smartCardData = {
        profile,
        cardProfile,
        citizenAddress,
        cardCitizenAddress,
        imageFile,
        attachment: imageFile,
        citizenId: cid,
        hn,
        patient,
        patientProfile,
        rawCardData: data,
        smartCardAction,
        hnSearchMatchType: searchMatchType,
      }
      setSmartCardDesignerValue('profile', profile)
      setSmartCardDesignerValue('cardProfile', cardProfile)
      setSmartCardDesignerValue('citizenAddress', citizenAddress)
      setSmartCardDesignerValue('cardCitizenAddress', cardCitizenAddress)
      setSmartCardImageDesignerData(imageFile)
      setSmartCardDesignerValue('citizenId', cid)
      setSmartCardDesignerValue('hn', hn)
      setSmartCardDesignerValue('patient', patient)
      setSmartCardDesignerValue('patientProfile', patientProfile)
      setSmartCardDesignerValue('smartCardData', smartCardData)
      setSmartCardDesignerValue('rawCardData', data)
      setSmartCardDesignerValue('smartCardAction', smartCardAction)
      setSmartCardDesignerValue('hnSearchMatchType', searchMatchType)
      if (onUpdateData) {
        onUpdateData(
          getSmartCardUpdateData({
            profile,
            cardProfile,
            citizenAddress,
            cardCitizenAddress,
            imageFile,
            citizenId: cid,
            hn,
            patient,
            patientProfile,
            smartCardData,
            smartCardAction,
            hnSearchMatchType: searchMatchType,
          }),
        )
      }
      onUpdateDesignerData(designerData)
      setResult(JSON.stringify(data, null, 2))
      setCardData(data)
      setTip('')
      setErrorMessage('')
    }
  }

  const postSmartCardLog = async (payload) => {
    await httpClient
      .put(`/pas/smart-card-log?cid=${payload.cid}`, {
        jsonData: payload,
        cid: payload.cid,
      })
      .then(({ data }) => data.data)
      .catch(() => [])
  }

  const handleSmartCardData = async (data) => {
    if (
      ['smart-card::inserted', 'scan-smart-card'].includes(data?.event) &&
      data?.status !== 'error' &&
      data?.data
    ) {
      let response = data.data
      let cid = response?.cid
      if (!cid || cid === processedSmartCardCidRef.current) return

      processedSmartCardCidRef.current = cid
      if (cid !== loggedSmartCardCidRef.current) {
        loggedSmartCardCidRef.current = cid
        await postSmartCardLog(response)
      }
      debounceCallback(convertCardData, [response])
    } else {
      setErrorMessage(data?.message)
      setMessage('')
      setTip(data?.tip)
    }
  }

  useEffect(() => {
    if (!isReadyApp) return undefined

    setMessage('รอสักครู่ กำลังอ่านข้อมูลจากบัตรประชาชน...')
    setTip('')
    setErrorMessage('')
    const socket = io('http://localhost:5985')
    socket.on('connect', async () => {
      console.log('Connected:', socket.id)
      socket.emit('register', { uuid: socket.id, connectionId: socket.id })

      const resp = (await axios.post('http://localhost:5985/smart-cards')).data
      console.log('resp', resp)
      if (resp.status == 'processing') {
        //setMessage('รอสักครู่ เรียกคำสั่งอ่านบัตรประชาชนเรียบร้อย...')
      }
    })

    // When disconnected (e.g., app closed)
    socket.on('disconnect', (reason) => {
      console.warn('⚠️ Socket disconnected:', reason)
      if (reason === 'io server disconnect' || reason === 'transport close') {
        // Desktop app likely closed
        // You can try to reconnect or handle it
        setMessage('แอปพลิเคชันเดสก์ท็อปถูกปิด กรุณาเปิดแอปใหม่อีกครั้ง')
        setErrorMessage()
        socket.disconnect()
        setIsReadyApp(false)
      }
    })

    socket.on('scan-smart-card', handleSmartCardData)

    socket.on('smart-card::inserted', handleSmartCardData)

    socket.on('smart-card::device-deactivated', (data) => {
      setErrorMessage(data?.message)
      setMessage('')
      setTip(data?.tip)
    })
    socket.on('smart-card::removed', async (data) => {
      if (data.event === 'smart-card::removed') {
        setMessage('บัตรประชาชนไม่ได้อยู่ในเครื่องอ่าน')
        setCardData({ ...DEFAULT_CARD_DATA })
        setTip('')
        setErrorMessage('')
        setResult('')
        setPatientList([])
        setSelectedPatient(null)
        setIsSearchingHN(false)
        setHasSearchedHN(false)
        setHnSearchMatchType('none')
        hnSearchMatchTypeRef.current = 'none'
        processedSmartCardCidRef.current = ''
        loggedSmartCardCidRef.current = ''
        addressTransformCacheRef.current = {}
        setSearchedCitizenId('')
        await axios.post('http://localhost:5985/smart-cards')
      }
    })

    socket.on('smart-card::error', (data) => {
      setErrorMessage(data?.message)
      setMessage('')
      setTip(data?.tip)
    })
    //smart-card::device-deactivated
    return () => {
      console.log('Disconnecting socket...')
      socket.disconnect()
    }
  }, [isReadyApp])

  const onClick = async () => {
    // const resp = (await axios.post('http://localhost:5985/smart-cards')).data
    // if (resp.status == 'processing') {
    //     setMessage('รอสักครู่ เรียกคำสั่งอ่านบัตรประชาชนเรียบร้อย...')
    // }
    setPatientList([])
    setSelectedPatient(null)
    setIsSearchingHN(false)
    setHasSearchedHN(false)
    setHnSearchMatchType('none')
    hnSearchMatchTypeRef.current = 'none'
    processedSmartCardCidRef.current = ''
    loggedSmartCardCidRef.current = ''
    addressTransformCacheRef.current = {}
    setSearchedCitizenId('')

    const ready = await ensureDesktopService({
      onStatus: ({ text }) => {
        setMessage(text)
        setTip('')
        setErrorMessage('')
      },
    })
    if (!ready.ok || !(await startReader())) {
      setMessage(DESKTOP_OFFLINE_MESSAGE)
    }
  }

  const closeCallback = async ({ hydratePatient = true } = {}) => {
    console.log('close callback')
    let hn = getDesignerHN()
    let addressUpdateData
    let profileUpdateData
    if (hydratePatient) {
      let person = null
      let personAddress = null
      if (hn) {
        let patient =
          getFirstValue(
            objectPath.get(designerData, designerKey + '.smartCardData.patient'),
            objectPath.get(designerData, 'smartCardData.patient'),
            objectPath.get(designerData, designerKey + '.patient'),
            objectPath.get(designerData, 'patient'),
          ) || { hn }
        if (String(objectPath.get(patient, 'hn')) !== String(hn)) {
          patient = { hn }
        }
        let hydratedPatient = await getHydratedPatient(patient)
        person =
          objectPath.get(hydratedPatient, 'patientProfile') ||
          getPatientPersonData(hydratedPatient)
        personAddress = objectPath.get(hydratedPatient, 'citizenAddress')
      }
      addressUpdateData = await updateTransformedCitizenAddressDesignerData(
        personAddress,
      )
      if (person) profileUpdateData = updateProfileDesignerData(person)
    }
    axios
      .post('http://localhost:5985/win/close', null, {
        headers: {
          accept: '*/*',
        },
      })
      .then((response) => {
        console.log('Success:', response.data)
      })
      .catch((error) => {
        console.error('Error:', error)
      })
    return {
      status: 200,
      message: 'success',
      data: { ...(addressUpdateData || {}), ...(profileUpdateData || {}) },
    }
  }

  useEffect(() => {
    let callback = {
      closeCallback: closeCallback,
    }
    if (onCreateCallback) {
      onCreateCallback(designerKey, callback)
    }
  }, [])

  const renderHNTable = () => {
    if (!isSearchingHN && !hasSearchedHN) return null
    let isNameOnlyMatch = hnSearchMatchType === 'fullName' && patientList.length > 0
    return (
      <div className="mt-3" style={{ width: '100%', maxWidth: '100%' }}>
        {isSearchingHN && (
          <p className="text-center mb-0">กำลังค้นหา HN...</p>
        )}
        {!isSearchingHN && patientList.length === 0 && (
          <p className="text-center mb-0">ไม่พบรายการ HN</p>
        )}
        {!isSearchingHN && isNameOnlyMatch && (
          <div className="alert alert-warning py-2 mb-2" role="alert">
            ไม่พบ HN ที่เลขบัตรตรงกับ {searchedCitizenId || '-'} แต่พบผู้ป่วยชื่อ-นามสกุลตรงกัน
            สามารถเลือก HN เดิม หรือเลือกเปิด HN ใหม่จากบัตรใบนี้
          </div>
        )}
        {!isSearchingHN && patientList.length > 0 && (
          <div style={{ width: '100%' }}>
            <table
              className="table table-sm table-striped table-hover mb-0"
              style={{
                width: '100%',
                tableLayout: 'fixed',
                whiteSpace: 'normal',
              }}
            >
              <colgroup>
                <col style={{ width: '7%' }} />
                <col style={{ width: '21%' }} />
                <col style={{ width: '16%' }} />
                <col style={{ width: '9%' }} />
                <col style={{ width: '14%' }} />
                <col style={{ width: '21%' }} />
                <col style={{ width: '12%' }} />
              </colgroup>
              <thead>
                <tr>
                  <th className="text-center">
                    เลือก
                  </th>
                  <th>เลขบัตร</th>
                  <th>วันเดือนปีเกิด</th>
                  <th>อายุ</th>
                  <th>HN</th>
                  <th>วันที่เปิด HN</th>
                  <th>สถานะบัตร</th>
                </tr>
              </thead>
              <tbody>
                {isNameOnlyMatch && (
                  <tr
                    onClick={handleCreateNewHn}
                    style={{
                      cursor: 'pointer',
                      backgroundColor: !selectedPatient ? '#e9f5ff' : undefined,
                    }}
                  >
                    <td className="text-center align-middle">
                      <input type="radio" checked={!selectedPatient} readOnly />
                    </td>
                    <td className="align-middle" style={{ overflowWrap: 'anywhere' }}>
                      {searchedCitizenId || '-'}
                    </td>
                    <td className="align-middle">
                      {formatDisplayDate(cardData.birth_date)}
                    </td>
                    <td className="align-middle">{getAgeText(cardData.birth_date)}</td>
                    <td className="align-middle">
                      <strong>เปิดใหม่</strong>
                    </td>
                    <td className="align-middle">-</td>
                    <td className="align-middle">
                      <span className="badge badge-primary">New HN</span>
                    </td>
                  </tr>
                )}
                {patientList.map((patient, index) => {
                  let birthDate = getPatientBirthDate(patient)
                  let status = getPatientCardStatus(patient)
                  let selected = isSelectedPatient(patient)
                  let isCitizenIdMismatched =
                    isNameOnlyMatch && !isPatientCitizenIdMatchedCard(patient)
                  return (
                    <tr
                      key={
                        objectPath.get(patient, 'patientId') ||
                        objectPath.get(patient, 'hn') ||
                        index
                      }
                      onClick={() => handleSelectPatient(patient)}
                      style={{
                        cursor: 'pointer',
                        backgroundColor: selected ? '#e9f5ff' : undefined,
                      }}
                    >
                      <td className="text-center align-middle">
                        <input type="radio" checked={selected} readOnly />
                      </td>
                      <td
                        className="align-middle"
                        style={{ overflowWrap: 'anywhere' }}
                      >
                        {getPatientCitizenId(patient)}
                        {isCitizenIdMismatched && (
                          <small className="text-warning d-block">
                            ชื่อตรง แต่เลขบัตรไม่ตรง
                          </small>
                        )}
                      </td>
                      <td className="align-middle">
                        {formatDisplayDate(birthDate)}
                      </td>
                      <td className="align-middle">{getAgeText(birthDate)}</td>
                      <td
                        className="align-middle"
                        style={{ overflowWrap: 'anywhere' }}
                      >
                        {objectPath.get(patient, 'hn') || '-'}
                      </td>
                      <td className="align-middle">
                        {formatHnCreatedAt(patient)}
                      </td>
                      <td className="align-middle">
                        {isCitizenIdMismatched ? (
                          <span className="badge badge-warning">เลขไม่ตรง</span>
                        ) : (
                          <span
                            className={`badge badge-${
                              status === 'Fake Id'
                                ? 'danger'
                                : 'success'
                            }`}
                          >
                            {status}
                          </span>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    )
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center', // แนวนอน
        justifyContent: 'center', // แนวตั้ง
        minHeight: 'auto', // เต็มจอ
        padding: '1rem',
      }}
    >
      <div style={{ width: '500px', maxWidth: '100%' }}>
        {tip && (
          <>
            <h5>คำแนะนำ</h5>
            <p className="mb-0">{tip}</p>
          </>
        )}
        {errorMessage && (
          <p>
            {tip ? 'เนื่องจาก ' : ''}
            {errorMessage}
          </p>
        )}
        <h5 className="text-center">{message}</h5>

        <div style={{ position: 'relative' }}>
          <ThaiIDCard data={cardData} formatDate={formatDisplayDate} />
          <span
            className="ml-auto mr-0"
            style={{
              position: 'absolute',
              bottom: '0',
              right: '0',
            }}
          >
            <Tooltip title="ดูข้อมูล">
              <IconButton onClick={() => setShowInfo(!showInfo)}>
                <InfoOutlined />
              </IconButton>
            </Tooltip>
          </span>
        </div>

        <Button className="mt-2" style={{ width: '100%' }} onClick={onClick}>
          <i className="fa fa-redo mr-2" />
          แสกนอีกครั้ง
        </Button>
        {showInfo && (
          <FormControl
            as="textarea"
            className="mt-2"
            rows={8}
            placeholder="ข้อมูลจากบัตรจะแสดงตรงนี้"
            value={result}
            style={{
              fontFamily: 'monospace', // เหมาะกับ JSON
              whiteSpace: 'pre-wrap', // ขึ้นบรรทัดถูกต้อง
              maxWidth: '100%',
            }}
          />
        )}
      </div>
      {renderHNTable()}
    </div>
  )
}

ScanSmartCard.defaultProps = {
  designerData: {
    profile: {},
    cardProfile: {},
    imageFile: [],
    attachment: [],
    citizenAddress: {},
    cardCitizenAddress: {},
    citizenId: '',
    hn: '',
    smartCardData: {},
    closeCallback: () => {},
  },
}

export default ScanSmartCard
